import React from 'react';
import FreeSourcesFormSC, {
  FreeSourcesFormTextInputSC,
  FreeSourcesFormErrorSC,
  SumLabel,
  SumText,
  SumWrapper,
} from './free-sources-form.styled';
import { Formik } from 'formik';
import { generateSchema } from './free-sources-form-rules';
import { HeadingTwo } from '../../typography';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import dlt from '../../../utils/default-lang';
import Button from '../../ui/button';
import SmallText from '../../typography/small-text';
import Checkbox from '../../ui/checkbox/checkbox';
import numeral from 'numeral';

const TABS = [
  {
    key: 2,
    label: 'requests_form_free_sources_account_type_installments',
  },
  {
    key: 1,
    label: 'requests_form_free_sources_account_type_deposits',
  },
];

const formInitialValues = {
  amount: '',
  iban: '',
  fee_schedule: false,
  provision_conflict: false,
  virtual_account_type_id: 2,
};

const FreeSourcesForm = ({ onSubmitForm, resourcesOverview, initialTabOpen }) => {
  const schema = React.useMemo(() => generateSchema(resourcesOverview), [resourcesOverview]);
  const initialValues = React.useMemo(() => {
    return initialTabOpen
      ? { ...formInitialValues, virtual_account_type_id: initialTabOpen }
      : formInitialValues;
  }, [initialTabOpen]);

  return (
    <FreeSourcesFormSC>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          onSubmitForm({
            amount: Number(values.amount.replace(/,/g, '.')),
            iban: values.iban.replace(/ /g, ''),
            virtual_account_type_id: values.virtual_account_type_id,
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          isValid,
          setFieldTouched,
        }) => {
          const ibanHasError = errors && errors.iban && touched.iban;
          const feeScheduleHasError = errors && errors.fee_schedule && touched.fee_schedule;
          const provisionConflictHasError =
            errors && errors.provision_conflict && touched.provision_conflict;

          return (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="top-text-wrap">
                  <HeadingTwo className="bold">
                    <FormattedHTMLMessage
                      id="investor_request_documents_first_title"
                      defaultMessage={dlt['investor_request_documents_first_title']}
                    />
                  </HeadingTwo>
                </div>
                <div className="full-block">
                  <SmallText className="smaller bolder input-label">
                    <FormattedMessage
                      id="requests_form_free_sources_account_type_label"
                      defaultMessage={dlt['requests_form_free_sources_account_type_label']}
                    />
                  </SmallText>
                  <div className="type-switch-wrap">
                    <div className="inside-wrap">
                      {TABS.map((type) => {
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              setFieldValue('virtual_account_type_id', type.key, false);
                              setTimeout(() => {
                                setFieldTouched(
                                  'amount',
                                  touched.amount || false,
                                  touched.amount || false
                                );
                              }, 100);
                            }}
                            key={type.id}
                            className={type.key === values.virtual_account_type_id ? 'active' : ''}
                          >
                            <FormattedMessage
                              id={type.label}
                              defaultMessage={dlt[`${type.label}`]}
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <SumWrapper>
                  <SumLabel>
                    <FormattedMessage
                      id="requests_form_free_sources_sum_label"
                      defaultMessage={dlt['requests_form_free_sources_sum_label']}
                    />
                  </SumLabel>
                  <SumText>
                    {numeral(
                      Number(
                        values.virtual_account_type_id === 1
                          ? resourcesOverview.free_from_deposits
                          : resourcesOverview.free_from_installments
                      )
                    ).format('0,00.00')}{' '}
                    €
                  </SumText>
                </SumWrapper>
                <div className="full-block">
                  <SmallText className="smaller bolder input-label">
                    <FormattedMessage
                      id="requests_form_free_sources_amount_label"
                      defaultMessage={dlt['requests_form_free_sources_amount_label']}
                    />
                  </SmallText>

                  <FreeSourcesFormTextInputSC
                    type="text"
                    name="amount"
                    placeholder=""
                    filled={values.amount}
                    hasError={errors.amount && touched.amount}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      const formattedV = value.replace(/[^0-9.]/g, '');
                      setFieldValue('amount', formattedV);
                    }}
                    onBlur={handleBlur}
                    value={values.amount.replace('.', ',') || ''}
                  />

                  {errors.amount && touched.amount ? (
                    <FreeSourcesFormErrorSC>
                      <FormattedMessage
                        id={errors.amount || 'requests_form_bad_value'}
                        defaultMessage={dlt[errors.amount || 'requests_form_bad_value']}
                        values={{
                          amount: numeral(
                            Number(
                              values.virtual_account_type_id === 1
                                ? resourcesOverview.free_from_deposits
                                : resourcesOverview.free_from_installments
                            )
                          ).format('0,00.00'),
                        }}
                      />
                    </FreeSourcesFormErrorSC>
                  ) : null}
                </div>

                <div className="full-block">
                  <SmallText className="smaller bolder input-label">
                    <FormattedMessage
                      id="requests_form_free_sources_iban_label"
                      defaultMessage={dlt['requests_form_free_sources_iban_label']}
                    />
                  </SmallText>

                  <FreeSourcesFormTextInputSC
                    type="text"
                    name="iban"
                    placeholder=""
                    filled={values.iban}
                    hasError={ibanHasError}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.iban || ''}
                  />

                  {ibanHasError ? (
                    <FreeSourcesFormErrorSC>
                      <FormattedMessage id={errors.iban} defaultMessage={dlt[errors.iban]} />
                    </FreeSourcesFormErrorSC>
                  ) : null}
                </div>
                <div className="full-block">
                  <Checkbox
                    name="fee_schedule"
                    hasError={feeScheduleHasError}
                    value={`${values.fee_schedule}`}
                    checked={values.fee_schedule || false}
                    onChange={handleChange}
                    withInsideContent
                  >
                    <FormattedHTMLMessage
                      id="requests_form_free_sources_fee_schedule_label"
                      defaultMessage={dlt['requests_form_free_sources_fee_schedule_label']}
                      values={{
                        link1: `${process.env.REACT_APP_WISHMAKER_LANDING_URI}/static/pdf/vseobecne_obchodne_podmienky.pdf`,
                        link2: `${process.env.REACT_APP_WISHMAKER_LANDING_URI}/static/pdf/sadzobnik_poplatkov.pdf`,
                      }}
                    />
                  </Checkbox>
                  {feeScheduleHasError ? (
                    <FreeSourcesFormErrorSC>
                      <FormattedMessage
                        id={errors.fee_schedule}
                        defaultMessage={dlt[errors.fee_schedule]}
                      />
                    </FreeSourcesFormErrorSC>
                  ) : null}
                </div>
                <div className="full-block">
                  <Checkbox
                    name="provision_conflict"
                    hasError={provisionConflictHasError}
                    value={`${values.provision_conflict}`}
                    checked={values.provision_conflict || false}
                    onChange={handleChange}
                    label="requests_form_free_sources_provision_conflict_label"
                  />
                  {provisionConflictHasError ? (
                    <FreeSourcesFormErrorSC>
                      <FormattedMessage
                        id={errors.provision_conflict}
                        defaultMessage={dlt[errors.provision_conflict]}
                      />
                    </FreeSourcesFormErrorSC>
                  ) : null}
                </div>
              </div>
              <div className="row">
                <div className="full-block">
                  <SmallText className="text-justify black-transparent">
                    {values.virtual_account_type_id === 1 ? (
                      <FormattedMessage
                        id="requests_form_tax_law_text"
                        defaultMessage={dlt['requests_form_tax_law_text_2']}
                      />
                    ) : (
                      <FormattedMessage
                        id="requests_form_tax_law_text"
                        defaultMessage={dlt['requests_form_tax_law_text_1']}
                      />
                    )}
                  </SmallText>
                </div>
              </div>
              <div className="row">
                <div className="full-block submit-button">
                  {/*    <BiggerText>
                    <FormattedMessage
                      id="requests_form_continue_hint"
                      defaultMessage={dlt['requests_form_continue_hint']}
                    />
                  </BiggerText> */}
                  <Button type="PRIMARY" className="form-submit" onClick={handleSubmit} withIcon>
                    <FormattedMessage
                      id="global_continue"
                      defaultMessage={dlt['global_continue']}
                    />
                  </Button>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </FreeSourcesFormSC>
  );
};

export default FreeSourcesForm;
